import React, { useState } from "react";
import { Link } from "react-router-dom";
import { projects, disciplines } from "../data/mock";
import { ArrowUpRight } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All Work" },
  ...disciplines.map((d) => ({ id: d.categoryId || d.id, label: d.title })),
];

const WorkGrid = () => {
  const [filter, setFilter] = useState("all");
  const list = filter === "all" ? projects : projects.filter((p) => p.categoryId === filter);

  return (
    <section
      id="work"
      className="px-6 md:px-10 max-w-[1400px] mx-auto py-20 md:py-28 border-t border-[#d8d5cc]"
    >
      {/* Header row */}
      <div className="grid grid-cols-12 gap-4 mb-10 md:mb-14">
        <div className="col-span-12 md:col-span-3">
          <p className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b64]">
            Selective Work / 03
          </p>
        </div>
        <div className="col-span-12 md:col-span-9 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="text-[32px] md:text-[54px] leading-[1.02] tracking-[-0.02em] text-[#0d0d0d] font-display max-w-3xl">
            Selective Work
          </h2>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`text-[12px] tracking-tight px-3 py-1.5 border transition-colors duration-200 ${
                  filter === f.id
                    ? "bg-[#0d0d0d] text-[#f5f3ee] border-[#0d0d0d]"
                    : "bg-transparent text-[#0d0d0d] border-[#d8d5cc] hover:border-[#0d0d0d]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-14 md:gap-y-20">
        {list.map((p, i) => {
          // vary column spans for editorial rhythm
          const patterns = [
            "md:col-span-7",
            "md:col-span-5",
            "md:col-span-5",
            "md:col-span-7",
            "md:col-span-6",
            "md:col-span-6",
          ];
          const span = patterns[i % patterns.length];

          // Show a horizontal divider between discipline groups (only on "All Work")
          const prev = i > 0 ? list[i - 1] : null;
          const showDivider =
            filter === "all" && prev && prev.categoryId !== p.categoryId;

          return (
            <React.Fragment key={p.id}>
              {showDivider && (
                <div className="md:col-span-12 -my-4 md:-my-6">
                  <div className="border-t border-[#d8d5cc]" />
                </div>
              )}
              <Link
                to={`/work/${p.id}`}
                className={`group relative ${span}`}
              >
              {/* Eyebrow row above the thumbnail */}
              <div className="flex items-center justify-between mb-3 text-[11px] tracking-[0.18em] uppercase text-[#6b6b64]">
                <span className="flex items-center gap-3">
                  <span>({p.index})</span>
                  <span>{p.category}</span>
                </span>
                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-[#0d0d0d]">
                  View →
                </span>
              </div>

              <div className="relative overflow-hidden bg-[#0d0d0d] aspect-[1870/1052]">
                <img
                  src={p.cover}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-1000 ease-out"
                />
                <div className="absolute inset-0 bg-[#0d0d0d] opacity-0 group-hover:opacity-10 transition-opacity duration-500" />

                {/* hover CTA arrow */}
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#f5f3ee] text-[#0d0d0d] flex items-center justify-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  <ArrowUpRight size={16} />
                </div>
              </div>

              {/* Caption */}
              <div className="mt-5">
                <h3 className="font-display text-[22px] md:text-[26px] leading-[1.1] tracking-[-0.01em] text-[#0d0d0d]">
                  {p.title}
                </h3>
                <p className="mt-1 italic font-serif text-[14px] text-[#6b6b64]">
                  {p.client} — {p.role}
                </p>
                {p.award && (
                  <p className="mt-2 text-[11px] tracking-[0.18em] uppercase text-[#0d0d0d]">
                    ★ {p.award}
                  </p>
                )}
              </div>
            </Link>
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
};

export default WorkGrid;
